import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, fileUrl, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Textarea, Select, StatusBadge, Attachment } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, MessageSquare, Send, ImagePlus, ArrowRight, MapPin, Sparkles, Camera, FileText, Upload } from "lucide-react";
import { ImportDialog } from "@/components/ImportDialog";
import { MediaUploader } from "@/components/MediaUploader";
import { CameraCapture } from "@/components/CameraCapture";
import { Lightbox } from "@/components/Lightbox";

const SERVICE_TYPES = ["Property Preservation", "Initial Services", "Inspection", "Occupancy Check", "Eviction", "REO Services", "Maid Refresh / Janitorial", "Lock Changes / Re-Key", "Debris Removal", "Winterization", "De-Winterization", "Grass Cut / Lawn Maintenance", "Securing / Board-Up", "Pool Securing", "Repairs", "Virtual Tour", "Mobile Notary", "Real Estate Photography", "Construction"];
const CLIENT_TYPES = ["Homeowner", "Realtor", "Investor", "Property Manager", "Bank/REO"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Requests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [crew, setCrew] = useState([]);

  const load = useCallback(() => api.get("/requests").then((r) => setRequests(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/crew").then((r) => setCrew(r.data)); }, [isAdmin]);

  const summarize = async (rid) => {
    try {
      const { data } = await api.post(`/ai/summarize-request/${rid}`);
      setRequests((prev) => prev.map((x) => (x.id === rid ? { ...x, ai_summary: data.summary } : x)));
      toast.success("AI summary ready");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createBid = async (rid) => {
    try {
      const { data } = await api.post(`/requests/${rid}/create-bid`);
      toast.success("Draft bid created from request — review, then Send to client");
      window.location.assign(`/bids?open=${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{isAdmin ? "Admin Inbox" : "Service Requests"}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{isAdmin ? "Requests" : "My Requests"}</h1>
        </div>
        {!isAdmin && <Btn data-testid="new-request-btn" onClick={() => setNewOpen(true)}><Plus size={16} /> New Request</Btn>}
        {isAdmin && <Btn variant="secondary" data-testid="import-requests-btn" onClick={() => setImportOpen(true)}><Upload size={16} /> Import CRM File</Btn>}
      </div>
      {isAdmin && <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} idPrefix="requests" title="Bulk Import Requests" endpoint="/imports/requests"
        templateName="requests_import_template.csv" buttonLabel="Import Requests" onDone={load}
        description="Upload a client-request export from your CRM (.csv or .xlsx). Each row becomes a new request in your inbox. Rows whose Client Email matches an existing client account are linked to that client so they can chat and approve quotes."
        columns="Client Email · Client Name · Service Type · Address · Notes · Client Type · Phone" />}

      {requests.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono">No requests yet.</Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {requests.map((r, i) => (
            <Card key={r.id} data-testid={`request-card-${r.id}`} className="p-5 animate-rise" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722] bg-[#FF5722]/10 border border-[#FF5722]/30 px-2 py-0.5">{r.service_type}</span>
                <StatusBadge status={r.status} />
              </div>
              <div className="flex items-start gap-2"><MapPin size={15} className="text-zinc-500 mt-0.5 shrink-0" /><h3 className="font-head font-bold text-white leading-tight">{r.address}</h3></div>
              {isAdmin && <div className="font-mono text-[11px] text-zinc-500 mt-1">{r.client_name} · {r.client_email}{r.contact_phone ? ` · ${r.contact_phone}` : ""}</div>}
              {isAdmin && (r.client_type || r.source === "public") && <div className="flex flex-wrap gap-1 mt-1">{r.client_type && <span className="font-mono text-[9px] uppercase bg-zinc-800 text-zinc-300 border border-black px-1.5 py-0.5">{r.client_type}</span>}{r.source === "public" && <span className="font-mono text-[9px] uppercase bg-[#FF5722] text-black border border-black px-1.5 py-0.5">No account</span>}{r.email_consent && <span className="font-mono text-[9px] uppercase bg-emerald-600 text-white border border-black px-1.5 py-0.5">Email OK</span>}{r.sms_consent && <span className="font-mono text-[9px] uppercase bg-blue-600 text-white border border-black px-1.5 py-0.5">SMS OK</span>}</div>}
              {r.scope_of_work && <div className="mt-2 text-xs bg-zinc-950 border border-zinc-800 p-2"><span className="font-mono uppercase text-[9px] tracking-widest text-[#FF5722]">Scope of Work</span><div className="text-zinc-300 mt-0.5 whitespace-pre-line">{r.scope_of_work}</div></div>}
              {isAdmin && r.estimate_line_items?.length > 0 && (
                <div className="mt-2 text-xs bg-[#FF5722]/5 border border-[#FF5722]/30 p-2" data-testid={`estimate-${r.id}`}>
                  <span className="font-mono uppercase text-[9px] tracking-widest text-[#FF5722] flex items-center gap-1"><Sparkles size={10} /> Client AI Estimate</span>
                  <div className="mt-1 space-y-0.5">
                    {r.estimate_line_items.map((it, idx) => (
                      <div key={`${it.name}-${idx}`} className="flex justify-between gap-2 text-zinc-300"><span>{it.name} <span className="text-zinc-500">×{it.quantity}{it.unit ? ` ${it.unit}` : ""}</span></span><span className="font-mono">${(Number(it.quantity || 0) * Number(it.unit_price || 0)).toFixed(2)}</span></div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-1 pt-1 border-t border-[#FF5722]/30 text-white font-bold"><span>Est. Total</span><span className="font-mono">${Number(r.estimate_total || 0).toFixed(2)}</span></div>
                </div>
              )}
              <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{r.notes || "No notes."}</p>
              {r.ai_summary && (
                <div className="mt-2 text-xs bg-[#FF5722]/5 border border-[#FF5722]/25 p-2" data-testid={`ai-summary-${r.id}`}>
                  <span className="font-mono uppercase text-[9px] tracking-widest text-[#FF5722] flex items-center gap-1"><Sparkles size={10} /> AI Summary</span>
                  <div className="text-zinc-300 mt-0.5">{r.ai_summary}</div>
                </div>
              )}
              {r.photo_ids?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {r.photo_ids.slice(0, 4).map((pid) => <Attachment key={pid} src={fileUrl(pid)} size={48} className="object-cover border border-zinc-800" />)}
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-zinc-800 flex gap-2 flex-wrap">
                <Btn variant="secondary" data-testid={`chat-btn-${r.id}`} onClick={() => setActive(r)} className="flex-1"><MessageSquare size={14} /> Chat</Btn>
                {isAdmin && <Btn variant="secondary" data-testid={`summarize-btn-${r.id}`} onClick={() => summarize(r.id)}><Sparkles size={14} /></Btn>}
                {isAdmin && <Btn variant="secondary" data-testid={`create-bid-btn-${r.id}`} onClick={() => createBid(r.id)}><FileText size={14} /> Bid</Btn>}
                {r.bid_token && <Btn variant="secondary" data-testid={`view-quote-${r.id}`} onClick={() => window.open(`/bid/${r.bid_token}`, "_blank")}><FileText size={14} /> {isAdmin ? "Quote" : "View Your Quote"}</Btn>}
                {isAdmin && r.status !== "converted" && <ConvertButton req={r} crew={crew} onDone={load} />}
                {r.job_id && <Btn variant="secondary" onClick={() => window.location.assign(`/jobs/${r.job_id}`)}>View Job</Btn>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {active && <ChatDialog request={active} onClose={() => setActive(null)} isAdmin={isAdmin} />}
      {newOpen && <NewRequestDialog onClose={() => setNewOpen(false)} onDone={load} />}
    </div>
  );
}

function ConvertButton({ req, crew, onDone }) {
  const [open, setOpen] = useState(false);
  const [crewId, setCrewId] = useState("");
  const [date, setDate] = useState("");
  const convert = async () => {
    try {
      await api.post(`/requests/${req.id}/convert`, { assigned_crew_id: crewId || null, scheduled_date: date || null });
      toast.success("Converted to job");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <>
      <Btn data-testid={`convert-btn-${req.id}`} onClick={() => setOpen(true)} className="flex-1"><ArrowRight size={14} /> To Job</Btn>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase">Convert to Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">{req.service_type} @ {req.address}</p>
            <div><Label>Schedule Date</Label><Input data-testid="convert-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><Label>Assign Crew</Label><Select data-testid="convert-crew" value={crewId} onChange={(e) => setCrewId(e.target.value)}><option value="">Unassigned</option>{crew.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></div>
            <Btn data-testid="convert-confirm-btn" onClick={convert} className="w-full">Create Job</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewRequestDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ service_type: SERVICE_TYPES[0], client_type: "Homeowner", contact_phone: "", address: "", notes: "", scope_of_work: "" });
  const [photoIds, setPhotoIds] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [lb, setLb] = useState(null);
  const estimateTotal = lineItems.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);

  const uploadFile = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { id: data.id };
  };

  const estimate = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/requests/estimate", { photo_ids: photoIds, service_type: form.service_type, client_type: form.client_type, notes: form.notes });
      if (data.scope) setForm((f) => ({ ...f, scope_of_work: data.scope }));
      setLineItems(Array.isArray(data.line_items) ? data.line_items : []);
      toast.success("Instant estimate ready");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/requests", { ...form, photo_ids: photoIds, estimate_line_items: lineItems, estimate_total: estimateTotal });
      toast.success("Work order submitted");
      onClose(); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head font-black uppercase">New Service Request</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Service Type</Label><Select data-testid="req-service" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>{SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}</Select></div>
            <div><Label>I am a</Label><Select data-testid="req-client-type" value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })}>{CLIENT_TYPES.map((c) => <option key={c}>{c}</option>)}</Select></div>
          </div>
          <div><Label>Property Address</Label><Input data-testid="req-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required /></div>
          <div><Label>Phone (for call back)</Label><Input data-testid="req-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea data-testid="req-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Describe what you need…" /></div>
          <div>
            <Label>Photos / Documents (images or PDF)</Label>
            <MediaUploader upload={uploadFile} onUploaded={(ids) => setPhotoIds((p) => [...p, ...ids])} />
            {photoIds.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-2">
                {photoIds.map((pid, i) => <Attachment key={pid} src={fileUrl(pid)} size={56} onClick={() => setLb(i)} className="object-cover border-2 border-zinc-800" />)}
              </div>
            )}
          </div>
          <div className="bg-[#09090b] border-2 border-[#FF5722]/40 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><Sparkles size={15} className="text-[#FF5722]" /> Instant Estimate</div>
              <button type="button" data-testid="req-estimate-btn" onClick={estimate} disabled={aiBusy} className="bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] font-head font-bold uppercase text-xs px-3 py-2 disabled:opacity-50">{aiBusy ? "Calculating…" : "Get Instant Estimate"}</button>
            </div>
            {lineItems.length > 0 && (
              <div className="mt-3" data-testid="req-estimate-result">
                <div className="divide-y divide-zinc-800 border border-zinc-800">
                  {lineItems.map((it, idx) => (
                    <div key={`${it.name}-${idx}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="text-zinc-200">{it.name} <span className="text-zinc-500 font-mono text-xs">×{it.quantity}{it.unit ? ` ${it.unit}` : ""}</span></span>
                      <span className="text-white font-mono">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between px-3 py-2 mt-2 bg-[#FF5722] text-black font-head font-black uppercase" data-testid="req-estimate-total">
                  <span>Estimated Total</span><span>{money(estimateTotal)}</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2 leading-snug">This is an automated estimate based on national &amp; standard pricing. It is <span className="text-zinc-300 font-semibold">not a final quote</span> — our team will confirm your final price after review.</p>
              </div>
            )}
          </div>
          <div><Label>Scope of Work</Label><Textarea data-testid="req-scope" value={form.scope_of_work} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })} placeholder="AI drafts this from your photos & details, or write it yourself" /></div>
          <Btn data-testid="req-submit-btn" type="submit" className="w-full">Submit Work Order</Btn>
        </form>
        <Lightbox items={photoIds.map((pid) => ({ src: fileUrl(pid), name: `attachment_${pid}` }))} index={lb} setIndex={setLb} onClose={() => setLb(null)} />
      </DialogContent>
    </Dialog>
  );
}

function ChatDialog({ request, onClose, isAdmin }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [cam, setCam] = useState(false);
  const [lb, setLb] = useState(null);
  const fileRef = useRef();
  const endRef = useRef();
  const scrollRef = useRef();
  const atBottomRef = useRef(true);
  const lastIdRef = useRef(null);
  const prevCountRef = useRef(0);
  const forceNextRef = useRef(false);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const load = useCallback(() => api.get(`/requests/${request.id}/messages`).then((r) => setMessages(r.data)).catch(() => {}), [request.id]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const count = messages.length;
    const lastId = count ? messages[count - 1].id : null;
    const firstLoad = prevCountRef.current === 0 && count > 0;
    const newArrived = lastId !== null && lastId !== lastIdRef.current;
    if (firstLoad || forceNextRef.current || (newArrived && atBottomRef.current)) {
      endRef.current?.scrollIntoView({ behavior: firstLoad ? "auto" : "smooth" });
      atBottomRef.current = true;
    }
    if (newArrived) forceNextRef.current = false;
    prevCountRef.current = count;
    lastIdRef.current = lastId;
  }, [messages]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPhotoId(data.id); toast.success("Photo ready to send");
  };

  const captureUpload = async (files) => {
    const f = files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPhotoId(data.id); toast.success("Photo ready to send");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !photoId) return;
    try {
      await api.post(`/requests/${request.id}/messages`, { text, photo_id: photoId });
      forceNextRef.current = true;
      setText(""); setPhotoId(null);
      const ta = document.querySelector('[data-testid="chat-input"]'); if (ta) ta.style.height = "";
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const aiSuggest = async () => {
    setAiBusy(true);
    try { const { data } = await api.post("/ai/reply", { kind: "request", ref_id: request.id }); setText(data.suggestion); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const photoMsgs = messages.filter((m) => m.photo_id);

  const respondQuote = async (action) => {
    try {
      await api.post(`/bids/public/${request.bid_token}/respond`, { action });
      toast.success(action === "approve" ? "Quote approved — thank you!" : "Quote declined");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white max-w-lg rounded-none flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="font-head font-black uppercase">{isAdmin ? request.client_name : "Chat with Dispatch"}</DialogTitle>
          <p className="font-mono text-[11px] text-zinc-500">{request.service_type} · {request.address}</p>
        </DialogHeader>
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto space-y-3 py-2 min-h-[240px]" data-testid="chat-messages">
          {messages.length === 0 && <div className="text-center text-zinc-600 font-mono text-sm py-10">No messages yet. Say hello 👋</div>}
          {messages.map((m) => {
            const mine = (m.sender_role === "admin") === isAdmin;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] border-2 p-2.5 ${mine ? "bg-[#FF5722] text-black border-black" : "bg-zinc-950 text-white border-zinc-800"}`}>
                  <div className="font-mono text-[9px] uppercase tracking-wide opacity-70 mb-0.5">{m.sender_name}</div>
                  {m.photo_id && <Attachment src={fileUrl(m.photo_id)} onClick={() => setLb(photoMsgs.findIndex((x) => x.id === m.id))} className="max-w-full mb-1 border border-black/20 cursor-pointer" />}
                  {m.text && <div className="text-sm break-words whitespace-pre-wrap">{m.text}</div>}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        {!isAdmin && request.bid_token && (
          <div className="flex gap-2 my-2" data-testid="chat-quote-actions">
            <Btn data-testid="approve-quote-btn" onClick={() => respondQuote("approve")} className="flex-1">Approve Quote</Btn>
            <Btn variant="secondary" data-testid="decline-quote-btn" onClick={() => respondQuote("decline")} className="flex-1">Decline</Btn>
          </div>
        )}
        {isAdmin && <button type="button" data-testid="chat-ai-suggest" onClick={aiSuggest} disabled={aiBusy} className="self-start flex items-center gap-1 text-[#FF5722] hover:text-[#F4511E] font-mono text-[10px] uppercase tracking-widest mb-1 disabled:opacity-50"><Sparkles size={12} /> {aiBusy ? "Thinking…" : "Suggest reply"}</button>}
        <form onSubmit={send} className="flex items-end gap-2 border-t-2 border-zinc-800 pt-3">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="chat-photo-input" />
          <button type="button" data-testid="chat-attach-btn" onClick={() => fileRef.current?.click()} className={`shrink-0 px-3 py-2 border-2 ${photoId ? "border-[#FF5722] text-[#FF5722]" : "border-zinc-700 text-zinc-400"}`}><ImagePlus size={18} /></button>
          <button type="button" data-testid="chat-camera-btn" onClick={() => setCam(true)} className="shrink-0 px-3 py-2 border-2 border-zinc-700 text-zinc-400 hover:border-[#FF5722] hover:text-[#FF5722]"><Camera size={18} /></button>
          <Textarea data-testid="chat-input" value={text} onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px"; }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }} rows={1} placeholder="Type a message…" className="flex-1 min-w-0 resize-y min-h-[40px] max-h-40 py-2" />
          <Btn data-testid="chat-send-btn" type="submit"><Send size={16} /></Btn>
        </form>
        <CameraCapture open={cam} onClose={() => setCam(false)} onCapture={captureUpload} />
        <Lightbox items={photoMsgs.map((m) => ({ src: fileUrl(m.photo_id), name: `photo_${m.id}` }))} index={lb} setIndex={setLb} onClose={() => setLb(null)} />
      </DialogContent>
    </Dialog>
  );
}
